import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FaTrash, FaPlus, FaImages } from "react-icons/fa";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";
import { useDispatch, useSelector } from "react-redux";
import { getAllCarouselImages } from "../redux/actions/contents";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import useAuth from "../useAuth";
import { deleteMediaFromServer } from "../utils/deleteMediaFromServer";
import PageName from "../Components/global/PageName";

export default function CarouselManager() {
  const dispatch = useDispatch();
  const { carouselsList, loading: carouselsLoading } = useSelector(
    (state) => state?.content
  );
  const { clientInfo } = useAuth();
  const { register, handleSubmit, reset } = useForm();
  const [previewCarousel, setPreviewCarousel] = useState(null);
  const [previewIcon, setPreviewIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllCarouselImages(1, 100, clientInfo?.clientId));
  }, [sync, dispatch, clientInfo]);

  const onSubmit = async (data) => {
    setLoading(true);
    if (data?.image && data?.image?.[0]) {
      const carouselImageFile = data?.image?.[0];
      const iconImageFile = data?.icon?.[0];

      let mediaUrl = await uploadMediaToServer(carouselImageFile, "null");
      let iconUrl = "";

      if (iconImageFile) {
        iconUrl = await uploadMediaToServer(iconImageFile, "null");
      }

      try {
        const { data: resData } = await axios.post(
          `${server}/carousel/new`,
          null,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: {
              title: data.title,
              description: data.description,
              imageUrl: mediaUrl,
              iconUrl: iconUrl,
            },
          }
        );
        toast.success(resData?.message || "Carousel slide created.");
        setLoading(false);
        setPreviewCarousel(null);
        setPreviewIcon(null);
        reset();
        setSync((prvState) => prvState + 1);
      } catch (e) {
        setLoading(false);
        toast.error(e?.response?.data?.message || e?.message);
      }
    }
  };

  const handleDelete = async (carouselId, imageUrl, iconIcon) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this carousel slide?"
    );
    if (!userRes) return null;

    try {
      setLoading(true);
      await deleteMediaFromServer(imageUrl);
      await deleteMediaFromServer(iconIcon);

      const { data: resData } = await axios.delete(
        `${server}/carousel/deleteById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            imageId: carouselId,
          },
        }
      );
      toast.success(resData?.message || "Carousel slide deleted.");
      setSync((prvState) => prvState + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <PageName
        title="Carousel Manager"
        subTitle="Upload and manage hero banner slideshow items."
      />

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Side */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 sticky top-24">
              <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
                <FaImages className="text-amber-600" /> Add New Carousel Slide
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                Upload high quality banner images and details for the homepage slider.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Banner File Upload */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Carousel Image File
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    {...register("image", { required: true })}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      setPreviewCarousel(URL.createObjectURL(e.target.files[0]))
                    }
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                  />
                  {previewCarousel && (
                    <img
                      src={previewCarousel}
                      alt="Carousel Preview"
                      className="mt-3 h-36 w-full object-cover rounded-xl border border-slate-200 shadow-2xs"
                    />
                  )}
                </div>

                {/* Icon Upload */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Icon Badge File (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    {...register("icon")}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      setPreviewIcon(URL.createObjectURL(e.target.files[0]))
                    }
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                  />
                  {previewIcon && (
                    <img
                      src={previewIcon}
                      alt="Icon Preview"
                      className="mt-2 h-12 w-12 object-cover rounded-lg border border-slate-200"
                    />
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Slide Title
                  </label>
                  <input
                    type="text"
                    {...register("title", { required: true })}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                    placeholder="e.g. Signature Bridal Artistry"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    {...register("description", { required: true })}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600"
                    rows={3}
                    placeholder="Enter slide description..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs tracking-wider uppercase shadow-md shadow-amber-600/20 transition-all disabled:opacity-50 mt-4"
                >
                  <FaPlus size={12} /> Add Carousel Slide
                </button>
              </form>
            </div>
          </div>

          {/* List Grid Side */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Existing Carousel Slides
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Active Slides: <span className="font-semibold text-amber-600">{carouselsList?.length || 0}</span>
                  </p>
                </div>
              </div>

              {carouselsList?.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No carousel slides found. Upload your first slide using the form on the left.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {carouselsList?.map((carousel, index) => (
                    <motion.div
                      key={carousel?.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden relative group hover:shadow-md transition-all"
                    >
                      <div className="relative h-44 overflow-hidden bg-slate-900">
                        <img
                          src={carousel?.imageUrl}
                          alt={carousel?.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <button
                          onClick={() =>
                            handleDelete(
                              carousel?.id,
                              carousel?.imageUrl,
                              carousel?.iconUrl
                            )
                          }
                          className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-md transition-all opacity-0 group-hover:opacity-100"
                          title="Delete Slide"
                        >
                          <FaTrash size={13} />
                        </button>
                      </div>

                      <div className="p-4 flex items-start gap-3">
                        {carousel?.iconUrl && (
                          <img
                            src={carousel?.iconUrl}
                            alt="icon"
                            className="w-10 h-10 rounded-lg border border-slate-200 object-cover flex-shrink-0"
                          />
                        )}
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">
                            {carousel?.title}
                          </h4>
                          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                            {carousel?.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(loading || carouselsLoading) && <Loading />}
    </div>
  );
}
